import {
    WhatsApp,
    WhatsAppButton,
    WhatsAppComponent,
    TemplateComponentButtonType,
    isBodyComponent,
    isHeaderComponent,
} from 'domains/content/types';

/**
 * Transforms numeric placeholders ({{1}}, {{2}}) back to merge tag names ({{first_name}})
 * using the templateFields mapping from the API response.
 */
export const transformNumericPlaceholdersToMergeTags = (
    text: string,
    templateFields: Record<string, string>
): string => {
    if (!text || !templateFields || Object.keys(templateFields).length === 0) {
        return text;
    }
    return text.replace(/\{\{(\d+)\}\}/g, (match, numericKey) => {
        const mergeTagName = templateFields[numericKey];
        return mergeTagName ? `{{${mergeTagName}}}` : match;
    });
};

/**
 * Extracts the text content from the BODY component of a WhatsApp template
 * and transforms numeric placeholders to readable merge tag names.
 */
export const getBodyComponentText = (
    whatsAppData: WhatsApp | { components?: WhatsAppComponent[] } | null,
    templateFields?: Record<string, string>
): string => {
    if (!whatsAppData?.components || !Array.isArray(whatsAppData.components)) {
        return '';
    }

    const bodyComponent = whatsAppData.components.find(isBodyComponent);
    const rawText = bodyComponent?.text || '';

    if (templateFields && Object.keys(templateFields).length > 0) {
        return transformNumericPlaceholdersToMergeTags(rawText, templateFields);
    }

    return rawText;
};

/**
 * Transforms text fields in all relevant components from numeric placeholders to merge tag names.
 * Handles BODY and TEXT-format HEADER components.
 */
export const transformComponentsWithMergeTags = (
    components: WhatsAppComponent[],
    templateFields: Record<string, string>
): WhatsAppComponent[] => {
    if (!templateFields || Object.keys(templateFields).length === 0) {
        return components;
    }

    return components.map((component) => {
        if (isBodyComponent(component) && component.text) {
            return { ...component, text: transformNumericPlaceholdersToMergeTags(component.text, templateFields) };
        }
        if (isHeaderComponent(component) && component.text) {
            return { ...component, text: transformNumericPlaceholdersToMergeTags(component.text, templateFields) };
        }
        return component;
    });
};

// ─── Button validation ────────────────────────────────────────────────────────

const CTA_BUTTON_TYPES = new Set<TemplateComponentButtonType>([
    TemplateComponentButtonType.URL,
    TemplateComponentButtonType.PHONE_NUMBER,
    TemplateComponentButtonType.FLOW,
    TemplateComponentButtonType.COPY_OFFER_CODE,
]);

const isCta = (b: WhatsAppButton) => CTA_BUTTON_TYPES.has(b.type as TemplateComponentButtonType);
const isQuickReply = (b: WhatsAppButton) => b.type === TemplateComponentButtonType.QUICK_REPLY;
const isOtp = (b: WhatsAppButton) => b.type === TemplateComponentButtonType.COPY_CODE;

/**
 * Returns a map of button index → error message for any invalid combinations.
 * An empty object means all buttons are valid.
 */
export const getButtonValidationErrors = (buttons: WhatsAppButton[]): Record<number, string> => {
    const errors: Record<number, string> = {};
    if (!buttons.length) return errors;

    const hasOtp = buttons.some(isOtp);

    // OTP must be standalone
    if (hasOtp && buttons.length > 1) {
        buttons.forEach((b, i) => {
            errors[i] = isOtp(b)
                ? 'OTP (Copy Code) cannot be combined with other button types'
                : 'Remove this button — OTP templates can only have one button';
        });
        return errors;
    }

    // Count by type
    const urlCount = buttons.filter((b) => b.type === TemplateComponentButtonType.URL).length;
    const phoneCount = buttons.filter((b) => b.type === TemplateComponentButtonType.PHONE_NUMBER).length;
    const flowCount = buttons.filter((b) => b.type === TemplateComponentButtonType.FLOW).length;

    if (urlCount > 2) {
        buttons.forEach((b, i) => {
            if (b.type === TemplateComponentButtonType.URL) errors[i] = 'Maximum 2 URL buttons allowed';
        });
    }
    if (phoneCount > 1) {
        buttons.forEach((b, i) => {
            if (b.type === TemplateComponentButtonType.PHONE_NUMBER)
                errors[i] = 'Maximum 1 Phone Number button allowed';
        });
    }
    if (flowCount > 1) {
        buttons.forEach((b, i) => {
            if (b.type === TemplateComponentButtonType.FLOW) errors[i] = 'Maximum 1 Flow button allowed';
        });
    }

    // No interleaving: CTA buttons must all come before Quick Reply buttons
    let seenQuickReply = false;
    buttons.forEach((b, i) => {
        if (isQuickReply(b)) {
            seenQuickReply = true;
        } else if (isCta(b) && seenQuickReply) {
            errors[i] = 'Call-to-action buttons must come before Quick Reply buttons';
        }
    });

    return errors;
};
